import { encodeURIParams } from '@/_utils/encode-uri-params';
import { camelToSnakeCase } from '@/_utils/string/camel-to-snake-case';
import { DataBagService } from '@/common/data-bag.service';
import { Data2Service } from '@/common/data2.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { AfterViewInit, Component, Input, OnChanges, ViewChild, input, output } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Modal } from 'bootstrap';
import { LoaderService } from '../../../common/loader/loader.service';
import {
	BagReservationListData,
	BagReservationListFilters,
	BagReservationListParameters,
} from '../bag-reservation-list.model';
import { ProcessReservationData } from '../process-reservation-list-data';
import { filtersToString } from '@/_utils/filters-to-string';
import { DatePipe } from '@angular/common';
import { DEFAULT_ORDERING } from '../bag-reservation-list.component';

export interface ColumnsDef {
	col: string;
	name: string;
	size: string;
	extraStyle?: Partial<CSSStyleDeclaration>;
	disableSorting?: boolean;
}

@Component({
	selector: 'app-bag-reservation-list-table',
	templateUrl: './bag-reservation-list-table.component.html',
	styleUrls: ['./bag-reservation-list-table.component.scss'],
})
export class BagReservationListTableComponent implements AfterViewInit, OnChanges {
	@ViewChild('dataTable') table: MatTable<any>;
	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@Input() elements: any[];
	@Input() columns: any[];

	readonly filters = input<BagReservationListFilters>();
	readonly parameters = input<BagReservationListParameters>();
	readonly allItemsSize = input(0);
	readonly page = output<PageEvent>();
	readonly sortedByColumn = output<string>();

	tableShowOptions = false;
	module = 'reservationList';
	itemsBeforeDataLoad = 0;
	sortCustomValue: string[] = ['promotionValue', 'left2pay', 'commissionValue', 'totalPrice'];

	matData = new MatTableDataSource<any>([]);
	viewIsSet = false;
	showTable = true;
	showColumns: string[] = [];
	settings = {
		hiddenColumns: [],
		size: 3260,
	};
	tableKeys: string[];

	constructor(
		private readonly loader: LoaderService,
		private readonly bag: DataBagService,
		private readonly infobox: InfoboxService,
		private readonly datePipe: DatePipe,
	) {}

	ngAfterViewInit(): void {
		this.customSorting();
		this.matData.paginator = this.paginator;
	}

	ngOnChanges(): void {
		if (!this.elements) {
			return;
		}
		this.settingsLoad();
		this.loadData();
	}

	pageChanged(event: PageEvent): void {
		this.page.emit(event);
	}

	loadData(): void {
		this.itemsBeforeDataLoad--;
		if (this.itemsBeforeDataLoad > 0) {
			return;
		}
		this.matData.data = this.elements;
		this.convertData(this.matData.data);
	}

	convertData(data): void {
		this.tableKeys = this.columns.map((obj) => obj.col);
		this.tableKeys.push('columnShow');
		this.matData.data = data;
		this.settingsLoad();
		this.generateColumns();
		this.loader.hide();
	}

	customSorting(): void {
		this.matData.sortingDataAccessor = (item, property) => {
			if (property === 'number') {
				return item[property].label;
			}
			if (this.sortCustomValue.includes(property)) {
				return +item[property].split(' ')[0];
			}
			return item[property];
		};
	}

	generateColumns(): void {
		this.showColumns = this.tableKeys.filter((obj) => !this.settings.hiddenColumns.includes(obj));
		this.setTableSize();
	}

	showHideRow(itemCol, colSize): void {
		if (!this.settings.hiddenColumns.includes(itemCol)) {
			this.settings.hiddenColumns.push(itemCol);
			this.settings.size = this.settings.size - +colSize;
		} else {
			this.settings.hiddenColumns.splice(this.settings.hiddenColumns.indexOf(itemCol), 1);
			this.settings.size = this.settings.size + +colSize;
		}

		this.setTableSize();
		this.settingsSave();
		this.loadData();
		this.viewIsSet = !!(this.settings.hiddenColumns && this.settings.hiddenColumns.length);
		this.loader.hide();
	}

	removeDuplicates(obj) {
		return [...new Set(obj)];
	}

	isArray(obj: any) {
		return Array.isArray(obj);
	}

	onSortChange(event: Sort): void {
		if (event.direction === '') {
			this.sortedByColumn.emit(DEFAULT_ORDERING);
		} else {
			this.sortedByColumn.emit(`${event.direction === 'asc' ? '' : '-'}${camelToSnakeCase(event.active)}`);
		}
	}

	dialogClose(): void {
		const modal = Modal.getInstance(document.getElementById('addModal'));
		if (!modal) {
			return;
		}
		modal.hide();
	}

	openDialog(): void {
		new Modal(document.getElementById('addModal')).show();
	}

	copyReservations(): void {
		const params = encodeURIParams({ ...this.filters(), offset: 0, limit: 99999 });
		this.loader.show();
		this.bag.get('/reservation/?' + params, (result, error) => {
			if (error) {
				this.infobox.error('Wystąpił błąd podczas pobierania danych do skopiowania');
				this.loader.hide();
				return;
			}
			const reservationData = new ProcessReservationData(this.datePipe).processReservationListData(result);
			this.copy2Clipboard(reservationData);
			this.loader.hide();
		});
	}

	private async copy2Clipboard(data: BagReservationListData[]): Promise<void> {
		const rows = data.map((row: any) => {
			const obj = {
				...row,
				id: row.id.label,
				statusName: row.statusName.name,
				product: this.removeDuplicates(row.product),
				stopDeparture: this.removeDuplicates(row.stopDeparture),
			};
			return Object.assign([], obj, {
				...this.tableKeys.map((header) => obj[header]),
			});
		});
		const table = [this.tableKeys, ...rows];
		const csvData = table.map((row) => row.join(';')).join('\n');
		try {
			window.focus();
			await navigator.clipboard.writeText(csvData);
			this.infobox.success('Dane zostały pobrane i skopiowane do schowka pomyślnie');
		} catch (err) {
			this.infobox.error('Wystąpił błąd podczas kopiowania do schowka');
		}
	}

	private settingsLoad(): void {
		if (!localStorage['pageSettings-' + this.module]) {
			return;
		}
		this.settings = JSON.parse(localStorage['pageSettings-' + this.module]);
		this.viewIsSet = !!(this.settings.hiddenColumns && this.settings.hiddenColumns.length);
	}

	private setTableSize(): void {
		const elements = ['mat-table', 'mat-paginator'];
		elements.forEach((elementId) => {
			const element = document.getElementById(elementId);

			if (!element) {
				return;
			}

			element.style.width = this.settings.size + 'px';
		});
	}

	private settingsSave(): void {
		localStorage.setItem('pageSettings-' + this.module, JSON.stringify(this.settings));
	}
}
